import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import { DevTool } from 'react-hook-form-devtools';
import Joi from '@hapi/joi';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import errorMessages from '../../assets/errorMessages';
import { formatAddress } from '../../helpers/formatters';

const validationResolver = (data) => {
  const errors = {};

  const errorList = Joi.object()
    .keys({
      other: Joi.string()
        .when('food_donor_name', {
          is: 'Other',
          then: Joi.string().required(),
        })
        .messages({
          'any.required': errorMessages.REQUIRED.message,
          'string.empty': errorMessages.REQUIRED.message,
        }),
    })
    .validate(
      {
        food_donor_name: data.food_donor ? data.food_donor.name : '',
        other: data.other,
      },
      { abortEarly: false, allowUnknown: true }
    );

  if (errorList.error) {
    errorList.error.details.forEach((error) => {
      errors[error.context.key] = { message: error.message };
    });
  }

  return {
    values: Object.keys(errors).length ? {} : data,
    errors: errors,
  };
};

const RegistrationMergeDialog = ({
  foodDonors = [],
  pickupLocations = {},
  isOpen,
  onClose,
  handleCancel,
  handleSave,
}) => {
  const {
    handleSubmit,
    register,
    control,
    watch,
    errors,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onBlur',
    validationResolver,
    defaultValues: {
      food_donor: '',
      other: '',
    },
  });

  const selectedChoice = watch('food_donor');
  const selectedPickupLocation = watch('pickup_location');
  const [inputValue, setInputValue] = useState('');
  const [foodDonorsOptions, setFoodDonorsOptions] = useState('');
  const selectedFoodDonorPickupLocations = selectedChoice?.id
    ? pickupLocations?.byFoodDonorId?.[selectedChoice.id]
    : [];
  const pickupLocationsOptions = [...selectedFoodDonorPickupLocations, { name: 'Add new pickup location', id: -1 }];
  const loadSuggestionsMinChars = 2;

  const onSubmit = (values) => {
    handleSave(values);
  };

  useEffect(() => {
    register('pickup_location');
  }, [register]);

  useEffect(() => {
    if (inputValue.length <= loadSuggestionsMinChars) {
      return setFoodDonorsOptions([]);
    }

    const filteredOptions = foodDonors.filter((option) =>
      option.name.toLowerCase().startsWith(inputValue.toLocaleLowerCase())
    );

    return setFoodDonorsOptions(filteredOptions);
  }, [inputValue]);

  useEffect(() => {
    // if (selectedFoodDonorPickupLocations.length) {
    //   return setValue('pickup_location', selectedFoodDonorPickupLocations[0]);
    // }

    return setValue('pickup_location', null);
  }, [selectedChoice]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Merge Request</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Please select the food donor who is requesting to add Donor Link to their account.
          </DialogContentText>

          <Controller
            name="food_donor"
            control={control}
            onChange={([event, data]) => data}
            as={
              <Autocomplete
                loading={!foodDonors.length}
                autoHighlight
                options={foodDonorsOptions}
                onInputChange={(event, value) => {
                  setInputValue(value);
                }}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="Type the 3 first letters of the Food Donor name"
                disableClearable
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body1">{option.name}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} placeholder="Select Food Donor" variant="outlined" />}
              />
            }
          />

          {selectedChoice && (
            <Box mt={2}>
              <Autocomplete
                loading={pickupLocations.inflight}
                value={selectedPickupLocation}
                autoHighlight
                options={pickupLocationsOptions}
                getOptionLabel={(option) =>
                  option.address
                    ? formatAddress({
                        address: option.address,
                        city: option.city,
                        st: option.st,
                        zip: option.zip,
                      })
                    : option.name
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableClearable
                onChange={(event, value) => setValue('pickup_location', value)}
                renderOption={(props, option) =>
                  option.address ? (
                    <li {...props}>
                      <Typography gutterBottom variant="body1">
                        {formatAddress({
                          address: option.address,
                          city: option.city,
                          st: option.st,
                          zip: option.zip,
                        })}
                      </Typography>
                    </li>
                  ) : (
                    <li {...props}>
                      <Typography variant="body1">{option.name}</Typography>
                    </li>
                  )
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select pickup location" variant="outlined" />
                )}
              />
            </Box>
          )}

          {selectedChoice && selectedChoice.name === 'Other' && (
            <Box mt={2} width={1}>
              <TextField
                fullWidth
                error={!!errors.other}
                helperText={errors.other ? errors.other.message : ''}
                label="Other"
                variant="outlined"
                inputRef={register()}
                name="other"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button disabled={isSubmitting || !selectedPickupLocation} type="submit">
            Continue
          </Button>
        </DialogActions>
        {process.env.NODE_ENV === 'development' && <DevTool control={control} />}
      </form>
    </Dialog>
  );
};

export default RegistrationMergeDialog;
